import { Link } from 'react-router-dom';
import { Shield, Home, ChevronRight, Mail, MapPin, ExternalLink } from 'lucide-react';

const PrivacyPageEN = () => {
  return (
    <div data-testid="privacy-page-en" className="min-h-screen bg-white pt-20">
      {/* Breadcrumb */}
      <div className="bg-slate-100 border-b border-slate-200">
        <div className="container-custom py-3">
          <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
            <Link to="/en" className="flex items-center gap-1 text-slate-500 hover:text-blue-600 transition-colors">
              <Home className="w-4 h-4" />
              <span>Home</span>
            </Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 font-medium">Privacy Policy</span>
          </nav>
        </div>
      </div>
      
      {/* Header */}
      <section className="py-12 bg-slate-50 border-b border-slate-200">
        <div className="container-custom">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
              <Shield className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight']">
                Privacy Policy
              </h1>
              <p className="text-slate-500 mt-1">Last updated: February 13, 2026</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-12">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-slate max-w-none">
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">1. Information About the Data Controller</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              This Privacy Policy applies to the processing of personal data within the AlbMove platform, operated by:
            </p>
            <ul className="list-none space-y-2 mb-6 bg-slate-50 rounded-xl p-6">
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Company:</span> <span className="text-slate-600">C.S.M. Sh.p.k.</span></li>
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Tax ID (NIPT):</span> <span className="text-slate-600">M21612019M</span></li>
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Registered:</span> <span className="text-slate-600">12/04/2022, Tirana</span></li>
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Administrator:</span> <span className="text-slate-600">Altin Shamku</span></li>
              <li className="flex items-center gap-3"><span className="font-semibold text-slate-700 w-44">Headquarters:</span> <span className="text-slate-600">Rruga Muçaj, Building 1, 1032, Vorë, Tirana, Albania</span></li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">In accordance with:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Law No. 9887/2008 "On Personal Data Protection", as amended</li>
              <li className="text-slate-600">Regulation (EU) 2016/679 "General Data Protection Regulation" (GDPR), when applicable</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              C.S.M. Sh.p.k. acts as the <strong>Data Controller</strong>.
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">2. Data Processing Principles</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We process personal data in accordance with Article 5 of GDPR, respecting:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li className="text-slate-600">Lawfulness, fairness and transparency</li>
              <li className="text-slate-600">Purpose limitation</li>
              <li className="text-slate-600">Data minimization</li>
              <li className="text-slate-600">Accuracy</li>
              <li className="text-slate-600">Storage limitation</li>
              <li className="text-slate-600">Integrity and confidentiality</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">3. Categories of Data We Collect</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.1 Identification Data</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">First and last name</li>
              <li className="text-slate-600">Email</li>
              <li className="text-slate-600">Phone number</li>
              <li className="text-slate-600">Identity document (when verification is required)</li>
              <li className="text-slate-600">Tax ID (for businesses)</li>
              <li className="text-slate-600">Physical address</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.2 Financial Data</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Card information (processed by licensed PSP providers)</li>
              <li className="text-slate-600">Transaction history</li>
              <li className="text-slate-600">Wallet balance</li>
              <li className="text-slate-600">Refund information</li>
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-slate-700 font-medium">AlbMove does not store complete payment card data.</p>
            </div>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.3 Charging Session Data</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Session date and time</li>
              <li className="text-slate-600">Station location</li>
              <li className="text-slate-600">Energy consumed (kWh)</li>
              <li className="text-slate-600">Duration</li>
              <li className="text-slate-600">Price</li>
              <li className="text-slate-600">Charger identifier</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.4 Technical Data</h3>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li className="text-slate-600">IP address</li>
              <li className="text-slate-600">Device ID</li>
              <li className="text-slate-600">Application version</li>
              <li className="text-slate-600">Operating system</li>
              <li className="text-slate-600">API logs</li>
              <li className="text-slate-600">WebSocket logs</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">4. Legal Bases for Processing (Article 6 GDPR)</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We process data based on the following legal bases:</p>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.1 Contract Performance (Article 6(1)(b) GDPR)</h3>
            <p className="text-slate-600 leading-relaxed mb-2">For:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Account creation</li>
              <li className="text-slate-600">Charging service provision</li>
              <li className="text-slate-600">Payment processing</li>
              <li className="text-slate-600">Invoice generation</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.2 Legal Obligation (Article 6(1)(c) GDPR)</h3>
            <p className="text-slate-600 leading-relaxed mb-2">In accordance with:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Law No. 87/2019 on fiscalization</li>
              <li className="text-slate-600">Albanian tax legislation</li>
              <li className="text-slate-600">Public authority requirements</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.3 Legitimate Interest (Article 6(1)(f) GDPR)</h3>
            <p className="text-slate-600 leading-relaxed mb-2">For:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Fraud prevention</li>
              <li className="text-slate-600">System security</li>
              <li className="text-slate-600">Performance monitoring</li>
              <li className="text-slate-600">OCPP infrastructure protection</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.4 Consent (Article 6(1)(a) GDPR)</h3>
            <p className="text-slate-600 leading-relaxed mb-2">For:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Marketing</li>
              <li className="text-slate-600">Analytics cookies</li>
              <li className="text-slate-600">Promotional communications</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              <strong>Consent can be withdrawn at any time.</strong>
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">5. Fiscalization and Tax Reporting</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              In accordance with Law No. 87/2019 and bylaws, each transaction is:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Registered</li>
              <li className="text-slate-600">Fiscalized through the DevPOS system</li>
              <li className="text-slate-600">Reported to tax administration</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              This processing is carried out based on legal obligation.
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">6. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed mb-4">In accordance with Article 5(1)(e) GDPR:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600"><strong>Account data:</strong> up to 5 years after closure</li>
              <li className="text-slate-600"><strong>Fiscal data:</strong> 10 years (legal obligation)</li>
              <li className="text-slate-600"><strong>Technical logs:</strong> 12 months</li>
              <li className="text-slate-600"><strong>Session data:</strong> 3–5 years for operational analysis</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              After the retention period expires, data is deleted or anonymized.
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">7. Data Security (Article 32 GDPR)</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We implement technical and organizational measures including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li className="text-slate-600">TLS encryption</li>
              <li className="text-slate-600">Server-side data encryption</li>
              <li className="text-slate-600">Two-factor authentication</li>
              <li className="text-slate-600">Role-based access control</li>
              <li className="text-slate-600">Continuous security monitoring</li>
              <li className="text-slate-600">Encrypted backups</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">8. International Data Transfer</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Data is stored within the EU. In case of transfer outside the EU, we apply:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li className="text-slate-600">European Commission adequacy decisions</li>
              <li className="text-slate-600">Standard Contractual Clauses (SCC) under Article 46 GDPR</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">9. Web Analytics and Logs</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">9.1 Google Analytics</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Used based on consent (Article 6(1)(a))</li>
              <li className="text-slate-600">IPs are anonymized where applicable</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">9.2 API & WebSocket Logs</h3>
            <p className="text-slate-600 leading-relaxed mb-2">Used for:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Security</li>
              <li className="text-slate-600">Audit</li>
              <li className="text-slate-600">Diagnostics</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">IPs are anonymized after 30 days.</p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">10. Cookies</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We use:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li className="text-slate-600">Essential cookies</li>
              <li className="text-slate-600">Analytics cookies (with consent)</li>
              <li className="text-slate-600">Functional cookies</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Management is done through the cookie banner.
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">11. Your Rights (Articles 12–22 GDPR)</h2>
            <p className="text-slate-600 leading-relaxed mb-4">You have the right to:</p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-800">Access (Article 15)</p>
                <p className="text-sm text-slate-500">Obtain a copy of your data</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-800">Rectification (Article 16)</p>
                <p className="text-sm text-slate-500">Correct inaccurate data</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-800">Erasure (Article 17)</p>
                <p className="text-sm text-slate-500">"Right to be forgotten"</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-800">Restriction (Article 18)</p>
                <p className="text-sm text-slate-500">Limit the use of your data</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-800">Portability (Article 20)</p>
                <p className="text-sm text-slate-500">Receive data in digital format</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-800">Objection (Article 21)</p>
                <p className="text-sm text-slate-500">Object to processing</p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6">
              <strong>Requests are processed within 30 days.</strong>
            </p>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">12. Complaints</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You have the right to file a complaint with:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <p className="font-semibold text-slate-800 mb-2">Commissioner for Information Rights and Personal Data Protection</p>
              <p className="text-slate-600 flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4" />
                Rruga "Abdi Toptani", Nr. 5, Tirana
              </p>
              <a href="https://www.idp.al" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                <ExternalLink className="w-4 h-4" />
                www.idp.al
              </a>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">13. Data Protection Officer</h2>
            <p className="text-slate-600 leading-relaxed mb-4">For any privacy-related matters:</p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <p className="text-slate-700 flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-blue-600" />
                <a href="mailto:support@albmove.al" className="text-blue-600 hover:underline font-medium">support@albmove.al</a>
              </p>
              <p className="text-slate-700 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Rruga Muçaj, Building 1, 1032, Vorë, Tirana, Albania
              </p>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mt-8 mb-4">14. Policy Changes</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              AlbMove reserves the right to update this policy in accordance with legal or technological developments.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">Material changes will be communicated through:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li className="text-slate-600">Email</li>
              <li className="text-slate-600">In-app notification</li>
              <li className="text-slate-600">Website publication</li>
            </ul>
            
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPageEN;
